import {css, StyleSheet} from 'aphrodite';
import React, {ChangeEvent, useState} from 'react';

import {MonomerNaturalAnalogue, MonomerVisualSymbol} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {TEXT_FIELD_BACKGROUND_COLOR, TEXT_FIELD_BORDER} from 'molbio/monomers/admin/util';
import {MonomerValidationErrors} from 'molbio/monomers/MonomersDAO';
import {FONT_PROXIMA_13, FONT_PROXIMA_18} from 'styles/FontConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {InputErrorMessageStyle, LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import ToggleGroup from 'ui-library/ToggleGroup';

import MonomerColorDropdown from '../MonomerColorDropdown';
import {
  DEFAULT_MODIFIED_BASE_COLOR,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  MONOMER_QUERY,
  MONOMER_TYPE_DISPLAY_NAME,
  NaturalAnalogueToNucleotideComponent,
  NATURAL_ANALOGUE_TO_MONOMER_TYPE,
  NucleotideComponent,
  ORDERED_ANALOGUES,
  ORDERED_DEGENERATE_ANALOGUES,
} from '../MonomerConstants';
import {gqlToAPINaturalAnalogue} from '../MonomersUtil';
import MonomerVisualSymbolDropdown from '../MonomerVisualSymbolDropdown';

interface MonomerManualCreationDefinePaneProps {
  name: string;
  onChangeName: (name: string) => void;
  symbol: string;
  onChangeSymbol: (name: string) => void;
  visualSymbol: MonomerVisualSymbol;
  onChangeVisualSymbol: (visual: MonomerVisualSymbol) => void;
  visualColor: string;
  onChangeVisualColor: (color: string) => void;
  naturalAnalogue: MonomerNaturalAnalogue;
  onChangeNaturalAnalogue: (naturalAnalogue: MonomerNaturalAnalogue) => void;
  validationErrors: MonomerValidationErrors;
}

export default function MonomerManualCreationDefinePane({
  name,
  onChangeName,
  symbol,
  onChangeSymbol,
  visualSymbol,
  onChangeVisualSymbol,
  visualColor,
  onChangeVisualColor,
  naturalAnalogue,
  onChangeNaturalAnalogue,
  validationErrors,
}: MonomerManualCreationDefinePaneProps): JSX.Element {
  const nucleotideComponent = NaturalAnalogueToNucleotideComponent[naturalAnalogue];
  const visualDropdown = (
    <BenchlingQuery<MonomerQuery>
      query={MONOMER_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      renderCachedDataWhenRefetching={true}
    >
      {({monomers}) => {
        const usedColors: Array<string> = Array.from(
          new Set(
            monomers
              .filter(
                (monomer) =>
                  ![DEFAULT_MODIFIED_BASE_COLOR, DEFAULT_MODIFIED_SUGAR_COLOR, null].includes(monomer.color)
              )
              .map((monomer) => monomer.color!)
          )
        );
        return nucleotideComponent === NucleotideComponent.PHOSPHATE ? (
          <MonomerVisualSymbolDropdown selectedSymbol={visualSymbol} onSelectSymbol={onChangeVisualSymbol} />
        ) : (
          <MonomerColorDropdown
            selectedColor={visualColor}
            nucleotideComponent={nucleotideComponent}
            onSelectColor={onChangeVisualColor}
            allUsedColors={usedColors}
          />
        );
      }}
    </BenchlingQuery>
  );
  const [showCommonOptions, setShowCommonOptions] = useState(ORDERED_ANALOGUES.includes(naturalAnalogue));
  const monomerType = MONOMER_TYPE_DISPLAY_NAME[NATURAL_ANALOGUE_TO_MONOMER_TYPE[naturalAnalogue]];

  const naturalAnalogueToggleGroup = showCommonOptions ? (
    <ToggleGroup
      size={ButtonSize.MEDIUM}
      options={ORDERED_ANALOGUES.map((na) => ({id: na, buttonProps: {text: gqlToAPINaturalAnalogue(na)}}))}
      selection={naturalAnalogue}
      onChangeSelection={onChangeNaturalAnalogue}
    />
  ) : (
    <ToggleGroup
      size={ButtonSize.MEDIUM}
      options={ORDERED_DEGENERATE_ANALOGUES.map((na) => ({
        id: na,
        buttonProps: {text: gqlToAPINaturalAnalogue(na)},
      }))}
      selection={naturalAnalogue}
      onChangeSelection={onChangeNaturalAnalogue}
    />
  );

  const textFieldWithLabel = (text: string, label: string): JSX.Element => (
    <div className={css(styles.flex1)}>
      <div className={css(styles.textFieldSubheader)}>
        <span>{label}</span>
      </div>
      <div className={css(styles.grayTextField)}>
        <span>{text}</span>
      </div>
    </div>
  );

  return (
    <div className={css(styles.paneContainer)}>
      <div className={css(styles.header)}>
        <span>Define new monomer settings</span>
      </div>
      <TextField
        className={css(styles.marginBottom16)}
        value={name}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChangeName(e.target.value);
        }}
        label="Name"
        labelStyle={LABEL_STYLES.LABEL}
        isRequired={true}
        autoFocus={true}
        dataTestAttrs={{
          component: 'MonomerManualCreationDefinePane',
          element: 'nameInput',
        }}
      />
      <div className={css(styles.textFieldSubheader)}>
        <span>Natural analog</span>
      </div>
      <ToggleGroup
        size={ButtonSize.MEDIUM}
        options={['Common', 'Degenerate'].map((text) => ({id: text, buttonProps: {text}}))}
        selection={showCommonOptions ? 'Common' : 'Degenerate'}
        onChangeSelection={(selection: string) => {
          setShowCommonOptions(selection === 'Common');
        }}
      />
      <div className={css(styles.naturalAnaloguesToggle)}>{naturalAnalogueToggleGroup}</div>
      <div className={css(styles.horizontalFieldsSection, styles.marginBottom16)}>
        <TextField
          className={css(styles.flex1)}
          value={symbol}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            onChangeSymbol(e.target.value);
          }}
          label="Symbol"
          labelStyle={LABEL_STYLES.LABEL}
          isRequired={true}
          hasError={validationErrors.symbol != null}
          error={
            validationErrors.symbol != null
              ? {
                  message: validationErrors.symbol,
                  style: InputErrorMessageStyle.INFO,
                }
              : null
          }
          dataTestAttrs={{
            component: 'MonomerManualCreationDefinePane',
            element: 'symbolInput',
          }}
        />
        <div className={css(styles.flex1)}>
          <div className={css(styles.visualSubheader, styles.flex1)}>
            <span>Visual</span>
          </div>
          <div className={css(styles.flex1)}>{visualDropdown}</div>
        </div>
      </div>
      <div className={css(styles.horizontalFieldsSection)}>
        {textFieldWithLabel(monomerType, 'Monomer type')}
        {textFieldWithLabel('RNA', 'Polymer type')}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  paneContainer: {
    marginLeft: 40,
    marginRight: 40,
  },
  header: {
    ...FONT_PROXIMA_18,
    marginTop: 18,
    marginBottom: 12,
  },
  horizontalFieldsSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  marginBottom16: {
    marginBottom: 16,
  },
  textFieldSubheader: {
    fontWeight: 'bold',
    marginBottom: 4,
    ...FONT_PROXIMA_13,
  },
  visualSubheader: {
    fontWeight: 'bold',
    marginBottom: 3,
    ...FONT_PROXIMA_13,
  },
  flex1: {
    flex: 1,
  },
  grayTextField: {
    padding: '3px 10px',
    background: TEXT_FIELD_BACKGROUND_COLOR,
    border: TEXT_FIELD_BORDER,
    boxSizing: 'border-box',
    borderRadius: '4px',
    height: 28,
    ...FONT_PROXIMA_13,
  },
  naturalAnaloguesToggle: {
    marginTop: 16,
    marginBottom: 16,
  },
  fullWidth: {
    width: '100%',
  },
});
